/**
 * The tester's execution form for one manual test — step-by-step results,
 * evidence uploads, observed result, verdict.
 *
 * Shared between the tester's "My tests" page and the run owner's execution
 * tab (where the owner records a result on the tester's behalf). The form
 * never decides the verdict for the tester: a failed step disables "Passed",
 * matching the backend rule, but the tester still chooses Failed or Blocked.
 */

import { useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Code,
  FileInput,
  Group,
  Paper,
  SegmentedControl,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconCheck,
  IconPaperclip,
  IconPlayerPlay,
  IconX,
} from "@tabler/icons-react";
import type { EvidenceOut, ManualResultIn, TestOut } from "@/shared/api/validation";
import { fmtDate } from "./stageMeta";

type StepResult = "pass" | "fail" | "na";

interface Props {
  test: TestOut;
  /** Uploads one evidence file; the caller refreshes the test afterwards. */
  onUpload: (file: File, note: string) => Promise<void>;
  onSubmit: (input: ManualResultIn) => Promise<void>;
  onStart?: () => Promise<void>;
  onDownloadEvidence?: (evidence: EvidenceOut) => void;
}

export function ManualResultForm({ test, onUpload, onSubmit, onStart, onDownloadEvidence }: Props) {
  const [results, setResults] = useState<Record<number, StepResult>>({});
  const [notes, setNotes] = useState<Record<number, string>>({});
  const [observed, setObserved] = useState("");
  const [verdict, setVerdict] = useState<ManualResultIn["verdict"]>("passed");
  const [file, setFile] = useState<File | null>(null);
  const [fileNote, setFileNote] = useState("");
  const [busy, setBusy] = useState<"upload" | "submit" | "start" | null>(null);

  const closed = ["passed", "failed", "blocked"].includes(test.status);
  const anyFail = useMemo(() => Object.values(results).includes("fail"), [results]);
  const allAnswered = test.steps.every((s) => results[s.n] !== undefined);

  const submit = async () => {
    setBusy("submit");
    try {
      await onSubmit({
        step_results: test.steps.map((s) => ({
          n: s.n,
          result: results[s.n] ?? "na",
          note: notes[s.n] || null,
        })),
        observed_result: observed.trim(),
        verdict: anyFail && verdict === "passed" ? "failed" : verdict,
      });
    } finally {
      setBusy(null);
    }
  };

  const upload = async () => {
    if (!file) return;
    setBusy("upload");
    try {
      await onUpload(file, fileNote.trim());
      setFile(null);
      setFileNote("");
    } finally {
      setBusy(null);
    }
  };

  return (
    <Stack gap="md">
      {test.preconditions && (
        <Paper withBorder radius="sm" p="sm" bg="var(--mantine-color-default-hover)">
          <Text size="xs" fw={600} c="dimmed" tt="uppercase">
            Preconditions
          </Text>
          <Text size="sm">{test.preconditions}</Text>
        </Paper>
      )}
      {test.test_data.length > 0 && (
        <Paper withBorder radius="sm" p="sm">
          <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={4}>
            Test data
          </Text>
          <Table withRowBorders={false} verticalSpacing={2}>
            <Table.Tbody>
              {test.test_data.map((d, i) => (
                <Table.Tr key={i}>
                  <Table.Td>
                    <Text size="sm">{d.item}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Code>{d.value ?? "—"}</Code>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      {test.status === "assigned" && onStart && (
        <Button
          variant="light"
          leftSection={<IconPlayerPlay size={16} />}
          loading={busy === "start"}
          onClick={() => {
            setBusy("start");
            void onStart().finally(() => setBusy(null));
          }}
          w="fit-content"
        >
          Start this test
        </Button>
      )}

      <Table verticalSpacing="sm" withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th w={40}>#</Table.Th>
            <Table.Th>Action</Table.Th>
            <Table.Th>Expected result</Table.Th>
            <Table.Th w={210}>Result</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {test.steps.map((s) => {
            const recorded = closed
              ? (test.step_results?.find((r) => r.n === s.n) as
                  | { result?: string; note?: string }
                  | undefined)
              : undefined;
            return (
              <Table.Tr key={s.n}>
                <Table.Td>
                  <Text fw={600} size="sm">
                    {s.n}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{s.action}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {s.expected}
                  </Text>
                </Table.Td>
                <Table.Td>
                  {closed ? (
                    <Stack gap={2}>
                      <ResultBadge value={recorded?.result} />
                      {recorded?.note && (
                        <Text size="xs" c="dimmed">
                          {recorded.note}
                        </Text>
                      )}
                    </Stack>
                  ) : (
                    <Stack gap={4}>
                      <SegmentedControl
                        size="xs"
                        fullWidth
                        value={results[s.n] ?? ""}
                        onChange={(v) => setResults({ ...results, [s.n]: v as StepResult })}
                        data={[
                          { label: "Pass", value: "pass" },
                          { label: "Fail", value: "fail" },
                          { label: "N/A", value: "na" },
                        ]}
                      />
                      <TextInput
                        size="xs"
                        placeholder="note (optional)"
                        value={notes[s.n] ?? ""}
                        onChange={(e) => setNotes({ ...notes, [s.n]: e.currentTarget.value })}
                      />
                    </Stack>
                  )}
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>

      <Paper withBorder radius="sm" p="sm">
        <Group justify="space-between" mb={6}>
          <Text size="sm" fw={600}>
            Evidence ({test.evidence.length})
          </Text>
        </Group>
        {test.evidence.length > 0 && (
          <Stack gap={4} mb="sm">
            {test.evidence.map((e) => (
              <Group key={e.evidence_id} gap="xs" wrap="nowrap">
                <IconPaperclip size={14} />
                {onDownloadEvidence ? (
                  <Text
                    size="sm"
                    c="brand"
                    style={{ cursor: "pointer" }}
                    onClick={() => onDownloadEvidence(e)}
                  >
                    {e.filename}
                  </Text>
                ) : (
                  <Text size="sm">{e.filename}</Text>
                )}
                <Text size="xs" c="dimmed">
                  {(e.size_bytes / 1024).toFixed(1)} KB · sha256 {e.sha256.slice(0, 12)} ·{" "}
                  {e.uploaded_by_display_name ?? "—"} · {fmtDate(e.created_at_utc)}
                </Text>
                {e.note && (
                  <Text size="xs" c="dimmed" fs="italic">
                    “{e.note}”
                  </Text>
                )}
              </Group>
            ))}
          </Stack>
        )}
        {!closed && (
          <Group align="flex-end" gap="xs">
            <FileInput
              size="xs"
              placeholder="Screenshot, export, log…"
              value={file}
              onChange={setFile}
              clearable
              style={{ flex: 1 }}
            />
            <TextInput
              size="xs"
              placeholder="what this shows"
              value={fileNote}
              onChange={(e) => setFileNote(e.currentTarget.value)}
              style={{ flex: 1 }}
            />
            <Button
              size="xs"
              variant="light"
              leftSection={<IconPaperclip size={14} />}
              disabled={!file}
              loading={busy === "upload"}
              onClick={() => void upload()}
            >
              Attach
            </Button>
          </Group>
        )}
      </Paper>

      {closed ? (
        <Paper withBorder radius="sm" p="sm">
          <Group gap="xs" mb={4}>
            <ResultBadge value={test.status} />
            <Text size="xs" c="dimmed">
              by {test.executed_by_display_name ?? "—"} · {fmtDate(test.executed_at_utc)}
            </Text>
          </Group>
          <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
            {test.observed_result}
          </Text>
        </Paper>
      ) : (
        <>
          <Textarea
            label="Observed result"
            description="What actually happened, in your words. This is the record."
            autosize
            minRows={3}
            value={observed}
            onChange={(e) => setObserved(e.currentTarget.value)}
            required
          />
          {anyFail && (
            <Alert variant="light" color="red" icon={<IconAlertTriangle size={16} />}>
              A step failed — the test cannot be recorded as passed.
            </Alert>
          )}
          <Group justify="space-between" align="flex-end">
            <SegmentedControl
              value={anyFail && verdict === "passed" ? "failed" : verdict}
              onChange={(v) => setVerdict(v as ManualResultIn["verdict"])}
              data={[
                { label: "Passed", value: "passed", disabled: anyFail },
                { label: "Failed", value: "failed" },
                { label: "Blocked", value: "blocked" },
              ]}
            />
            <Button
              leftSection={<IconCheck size={16} />}
              disabled={!observed.trim() || !allAnswered}
              loading={busy === "submit"}
              onClick={() => void submit()}
            >
              Record result
            </Button>
          </Group>
          {!allAnswered && (
            <Text size="xs" c="dimmed" ta="right">
              Give every step a result before recording.
            </Text>
          )}
        </>
      )}
    </Stack>
  );
}

export function ResultBadge({ value }: { value: string | undefined }) {
  const v = (value ?? "").toLowerCase();
  const color =
    v === "pass" || v === "passed"
      ? "green"
      : v === "fail" || v === "failed"
        ? "red"
        : v === "blocked" || v === "error"
          ? "orange"
          : "gray";
  const icon =
    color === "green" ? <IconCheck size={12} /> : color === "red" ? <IconX size={12} /> : null;
  return (
    <Badge size="sm" variant="light" color={color} leftSection={icon}>
      {value ?? "—"}
    </Badge>
  );
}
